/**
 * Aero Sites, per-site detail page (detail).
 *
 * The detail half of the master-detail pair that mirrors User Testing
 * (UserTestingDashboard -> UserTestingProject). Routed at /sites/:siteId where
 * :siteId is the url doc id, the same way UserTestingProject keys off
 * :projectId. The doc id is assigned from the slug at creation time but
 * serving (api/site.js) and analytics (api/plausible.js) resolve sites by the
 * generatedUrl FIELD, so the id never has to change when the slug does.
 *
 * The page is one page (SiteOnePage): preview, drop zone, tests, versions and
 * visits on the left; address, domain and settings on the right. The old
 * /:tab URLs still resolve and scroll to the matching section. The header
 * (title, Live badge, URL row with Copy and Visit) stays above.
 *
 * It loads that one site and its versions subcollection directly from
 * Firestore by id, so a direct/cold load of the URL renders rather than
 * blanking, and, exactly like UserTestingProject, it returns null while
 * loading and an empty state if the site can't be found.
 *
 * Deploy state (dropzone handler, auto-publish, progress) lives here rather
 * than in the Overview tab so an in-flight deploy keeps running and reporting
 * while the user scrolls the page.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import firebase from '../../firebase';
import { gatherFiles } from '../../utils/htmlDeploy/gatherFiles';
import { deploySite } from '../../utils/htmlDeploy/deploySite';
import { buildSiteUrl } from '../../utils/domainUtils';
import SiteOnePage, { SECTION_IDS } from './SiteOnePage';
import SiteCreatePanel from './SiteCreatePanel';
import SiteDetailHead from './SiteDetailHead';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import GlassButton from '../../components/Aero/GlassButton';
import AeroStatePanel, { stateFromError, errorLine } from '../../components/Aero/AeroStatePanel';
import { testMenuItem, testThisSite, canTestSite } from './testThisSite';
import { listTestCopies, siteTestRows } from '../UserTesting/testCopy';
import AeroFigmaSourceEditor from './AeroFigmaSourceEditor';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroSites.css';

const TAB_IDS = ['overview', 'prototype', 'versions', 'domain', 'analytics', 'settings'];

function tabFromPath(pathname) {
  for (const id of TAB_IDS) {
    if (pathname.endsWith(`/${id}`)) return id;
  }
  return null;
}

function AeroSiteDetail() {
  const { user, subscriptionType, openUpgradeModal, shell = {}, siteAccess = null } = useOutletContext();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const db = firebase.firestore();

  const [site, setSite] = useState(null);
  // The site's REAL owner uid. For an owned site this is the logged-in user;
  // for a site shared with this admin it is the actual owner, and EVERY write
  // (deploy, publish, rename, settings, delete) must target this path, not the
  // logged-in admin's. Null until the site resolves.
  const [ownerUid, setOwnerUid] = useState(null);
  const [isShared, setIsShared] = useState(false);
  const [loading, setLoading] = useState(true);
  // null, or a state kind for AeroStatePanel: 'notfound' | 'noaccess' | 'offline' | 'error'
  const [error, setError] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [autoPublish, setAutoPublish] = useState(true);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [copied, setCopied] = useState(false);
  // Tests of this site (test copies by sourceSiteId, plus an older test kept
  // on the site's own doc). Null until read; owned html sites only.
  const [siteTests, setSiteTests] = useState(null);
  const [testing, setTesting] = useState(false);

  // Inline (Notion-style) title editing for the page heading.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Old tab URLs (/versions, /domain, /analytics, /settings, /prototype)
  // still work: the page is one page, so they scroll to that section.
  const scrollTab = tabFromPath(location.pathname);
  const scrolledFor = useRef(null);
  const scrollToSection = useCallback((tabId) => {
    const el = document.getElementById(SECTION_IDS[tabId]);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  // Scroll once per URL, after the site has loaded and its sections rendered.
  const siteReady = !!site && (site.type === 'figma' ? !!(site.urls && (site.urls.figmaDesktopUrl || site.urls.figmaMobileUrl)) : !!site.activeVersion);
  useEffect(() => {
    if (!siteReady || !scrollTab || scrolledFor.current === location.pathname) return;
    scrolledFor.current = location.pathname;
    if (scrollTab === 'overview') return;
    const t = setTimeout(() => scrollToSection(scrollTab), 150);
    return () => clearTimeout(t);
  }, [siteReady, scrollTab, location.pathname, scrollToSection]);

  // Load the single site doc directly by id so a direct/cold load of the
  // detail URL renders (mirrors useProjectData fetching the project by id).
  const fetchSite = useCallback(async () => {
    if (!user?.uid || !siteId) {
      setLoading(false);
      return;
    }
    try {
      // Try the caller's own path first.
      let data = null;
      let resolvedOwner = user.uid;
      let sharedFlag = false;
      const ownedDoc = await db
        .collection('user').doc(user.uid)
        .collection('url').doc(siteId)
        .get();
      if (ownedDoc.exists) {
        data = ownedDoc.data();
      } else {
        // Not owned — it may be a site shared with this admin. Find it among
        // the url docs whose sharedWith contains my uid (collection-group),
        // matching by doc id, and stamp its real owner uid from the path.
        const sharedSnap = await db
          .collectionGroup('url')
          .where('sharedWith', 'array-contains', user.uid)
          .get()
          .catch(() => ({ docs: [] }));
        const match = sharedSnap.docs.find((d) => d.id === siteId);
        if (match) {
          data = match.data();
          resolvedOwner = match.ref.parent.parent.id;
          sharedFlag = true;
        }
      }
      // html sites plus figma-embed sites created through Aero Sites (the
      // aeroSite flag keeps plain Figmafolio folios out of this page).
      const isSiteDoc =
        data && (data.type === 'html' || (data.type === 'figma' && data.aeroSite));
      if (!isSiteDoc) {
        setError('notfound');
        return;
      }
      // A test copy is a test: its page is under Tests.
      if (data.sourceSiteId) {
        navigate(`/user-testing/${siteId}`, { replace: true });
        return;
      }
      setOwnerUid(resolvedOwner);
      setIsShared(sharedFlag);
      setSite({ id: siteId, ownerUid: resolvedOwner, shared: sharedFlag, ...data });
    } catch (err) {
      console.error('Error loading site:', err);
      setError(stateFromError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteId]);

  const fetchVersions = useCallback(async () => {
    const owner = ownerUid || user?.uid;
    if (!owner || !siteId) return;
    setVersionsLoading(true);
    try {
      const snapshot = await db
        .collection('user').doc(owner)
        .collection('url').doc(siteId)
        .collection('versions')
        .orderBy('createdAt', 'desc')
        .get();
      setVersions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading versions:', err);
    } finally {
      setVersionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUid, user, siteId]);

  useEffect(() => {
    fetchSite();
  }, [fetchSite]);

  // Tests of this site, once the site is known to be an owned, live html site.
  const siteIdForTests = site && !site.shared && site.type === 'html' ? site.id : null;
  useEffect(() => {
    if (!siteIdForTests || !user?.uid) return undefined;
    let cancelled = false;
    listTestCopies(user.uid, siteIdForTests)
      .then((copies) => { if (!cancelled) setSiteTests(siteTestRows(site, copies)); })
      .catch((err) => {
        console.error('Error loading tests of this site:', err);
        if (!cancelled) setSiteTests(siteTestRows(site, []));
      });
    return () => { cancelled = true; };
    // site is read for its own older test only; re-read when the site changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteIdForTests, user, site?.userTesting?.enabled]);

  // Versions load once the owner uid is resolved (owned -> own uid, shared ->
  // the real owner), so a shared site reads versions from the owner's path.
  useEffect(() => {
    if (ownerUid) fetchVersions();
  }, [ownerUid, fetchVersions]);

  useEffect(() => {
    document.title = site ? `${site.title || site.generatedUrl} | Aero Sites` : 'Sites | Aero';
  }, [site]);

  // html sites serve on their own subdomain (api/site.js); figma-embed sites
  // serve through the folio renderer at the path URL (App.js DynamicPage), or
  // on their custom domain when one is connected (DynamicPage2 resolves by the
  // customDomain field). buildSiteUrl encodes both rules; brand 'aero' pins
  // the aeroapp.ai host regardless of where the dashboard itself is served.
  const isFigmaSite = site?.type === 'figma';
  const liveUrl = site
    ? buildSiteUrl({
        generatedUrl: site.generatedUrl,
        type: site.type,
        customDomain: site.customDomain,
        brand: 'aero',
      })
    : '';
  const liveHost = liveUrl.replace(/^https:\/\//, '');

  const liveLinkToast = (host) => (
    <span>
      Live at{' '}
      <a href={`https://${host}`} target="_blank" rel="noopener noreferrer">
        {host}
      </a>
    </span>
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!site) {
        toast.error('Site not loaded yet.');
        return;
      }
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      setDeploying(true);
      setProgress({ phase: 'hashing', hashed: 0, total: acceptedFiles.length });
      try {
        const { files, homePage } = await gatherFiles(acceptedFiles);
        if (files.length === 0) {
          toast.error('No publishable files found in that drop.');
          return;
        }
        const result = await deploySite({
          // Shared sites deploy to the OWNER's path (versions + blobs), not the
          // logged-in admin's.
          uid: ownerUid || user.uid,
          urlId: site.id,
          files,
          homePage,
          serveMode: 'multipage',
          makeLive: autoPublish,
          onProgress: setProgress,
        });
        await fetchSite();
        await fetchVersions();
        const host = `${site.generatedUrl}.aeroapp.ai`;
        if (autoPublish) {
          toast.success(
            <span>
              Published {result.fileCount} files ·{' '}
              <a href={`https://${host}`} target="_blank" rel="noopener noreferrer">
                {host}
              </a>
            </span>,
            { autoClose: 8000 }
          );
        } else {
          toast.info('Saved as a draft. Publish it from Versions when you’re ready.', {
            autoClose: 8000,
          });
        }
      } catch (err) {
        console.error('Deploy failed:', err);
        toast.error('Publish failed. ' + errorLine(err));
      } finally {
        setDeploying(false);
        setProgress(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [site, user, ownerUid, autoPublish]
  );

  const publishVersion = async (versionId) => {
    if (!site || publishingId) return;
    const owner = ownerUid || user.uid;
    setPublishingId(versionId);
    try {
      const versionSnap = await db
        .collection('user').doc(owner)
        .collection('url').doc(site.id)
        .collection('versions').doc(versionId)
        .get();
      if (!versionSnap.exists) {
        toast.error('This version no longer exists.');
        await fetchVersions();
        return;
      }
      const vData = versionSnap.data() || {};
      await db
        .collection('user').doc(owner)
        .collection('url').doc(site.id)
        .set(
          {
            type: 'html',
            activeVersion: versionId,
            homePage: vData.homePage,
            serveMode: vData.serveMode || 'multipage',
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      await fetchSite();
      await fetchVersions();
      toast.success(liveLinkToast(`${site.generatedUrl}.aeroapp.ai`), { autoClose: 8000 });
    } catch (err) {
      console.error('Publish failed:', err);
      toast.error('Could not publish. ' + errorLine(err));
    } finally {
      setPublishingId(null);
    }
  };

  // Discarding a draft only deletes the version doc. Blobs are shared and
  // content-addressed, so they are never touched here.
  const discardDraft = async (versionId) => {
    if (!site) return;
    const owner = ownerUid || user.uid;
    const ok = window.confirm('Discard this draft? This cannot be undone.');
    if (!ok) return;
    try {
      // The Draft badge is computed from local state that can go stale, so
      // re-read the url doc and refuse to delete the version the site is
      // actually serving from.
      const urlSnap = await db
        .collection('user').doc(owner)
        .collection('url').doc(site.id)
        .get();
      const freshActive = urlSnap.exists ? urlSnap.data().activeVersion : null;
      if (versionId === freshActive) {
        toast.error('This version is live. Publish another version first, then discard this one.');
        await fetchSite();
        await fetchVersions();
        return;
      }
      await db
        .collection('user').doc(owner)
        .collection('url').doc(site.id)
        .collection('versions').doc(versionId)
        .delete();
      await fetchVersions();
      toast.info('Draft discarded.');
    } catch (err) {
      console.error('Discard failed:', err);
      toast.error('Could not discard. ' + errorLine(err));
    }
  };

  // Test this site: a new test copy, then its Setup (testThisSite.js).
  const runTestThisSite = async (s) => {
    if (testing) return;
    setTesting(true);
    try {
      await testThisSite({ uid: user?.uid, site: s, host: liveHost, navigate, refresh: shell.refresh });
    } catch (err) {
      console.error('Could not start the test:', err);
      toast.error('Could not start the test. ' + errorLine(err));
      setTesting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Inline title editing: click the heading to edit, save on blur/Enter,
  // cancel on Escape. Writes the title field ONLY (never the slug/URL) with the
  // same merge write the Settings rename uses. An empty title is ignored so the
  // heading reverts to the prior value.
  const startTitleEdit = () => {
    if (savingTitle) return;
    setTitleDraft(site.title || '');
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!editingTitle || savingTitle) return;
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === (site.title || '')) return;
    setSavingTitle(true);
    try {
      await db
        .collection('user').doc(ownerUid || user.uid)
        .collection('url').doc(site.id)
        .set({ title: trimmed, updatedAt: new Date() }, { merge: true });
      await fetchSite();
    } catch (err) {
      console.error('Error renaming site:', err);
      toast.error('Could not rename site. ' + errorLine(err));
    } finally {
      setSavingTitle(false);
    }
  };

  const onTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTitle(false);
    }
  };

  // Mirror UserTestingProject's cold-load handling: render nothing while the
  // single site doc is still loading, and a clear empty state if it's missing.
  if (loading) {
    return null;
  }

  if (error || !site) {
    const kind = error || 'notfound';
    const retry = () => { setError(null); setLoading(true); fetchSite(); };
    return (
      <div className="aero-main aero-sites-page">
        <AeroPageHeader crumbs={[{ label: 'Sites', onClick: () => navigate('/sites') }, { label: 'Site' }]} />
        <AeroStatePanel
          kind={kind}
          title={kind === 'notfound' ? 'Site not found' : kind === 'noaccess' ? "You don't have access to this site" : undefined}
          actions={(kind === 'offline' || kind === 'error') ? (
            <>
              <GlassButton fill="ink" size="sm" onClick={retry}>Try again</GlassButton>
              <GlassButton fill="glass" size="sm" onClick={() => navigate('/sites')}>All sites</GlassButton>
            </>
          ) : (
            <GlassButton fill="ink" size="sm" onClick={() => navigate('/sites')}>All sites</GlassButton>
          )}
        >
          {kind === 'notfound' ? 'It may have been deleted, or the link may be wrong.' : undefined}
        </AeroStatePanel>
      </div>
    );
  }

  // A figma site is live once a prototype is embedded; an html site once a
  // version is active.
  const isLive = isFigmaSite
    ? !!(site.urls && (site.urls.figmaDesktopUrl || site.urls.figmaMobileUrl))
    : !!site.activeVersion;

  // Figma sites have no version history, so Versions stays html-only; their
  // source is edited in the Prototype tab (html sites keep the source card in
  // Settings instead). Domain applies to BOTH types now: html gets subdomain +
  // custom domain, figma gets the custom domain card only (path-served
  // otherwise). Analytics applies to both: a figma site serves at
  // aeroapp.ai/{generatedUrl} and is tracked by page path, so its Analytics tab
  // uses the same path-based Plausible filter Figmafolio figma folios use
  // (api/plausible.js buildFilter keys off the doc's type; no proxy change).
  const tabContext = {
    user,
    // The site's real owner uid, so tab writes target the owner's path for a
    // shared site instead of the logged-in admin's.
    ownerUid: ownerUid || user.uid,
    isShared,
    subscriptionType,
    openUpgradeModal,
    site,
    versions,
    versionsLoading,
    publishingId,
    publishVersion,
    discardDraft,
    fetchSite,
    fetchVersions,
    liveUrl,
    liveHost,
    deploying,
    progress,
    autoPublish,
    setAutoPublish,
    onDrop,
    onShowVersions: () => scrollToSection('versions'),
    siteTests,
    onTestSite: canTestSite(site) ? () => runTestThisSite(site) : null,
    testing,
  };

  // Until the site is configured (a Figma prototype embedded, or an html
  // version live) it shows only its setup surface and no tab bar, so a fresh
  // site lands in setup instead of an empty multi-tab shell. A Figma site
  // collects its viewport links here; an html site gets the three publish
  // paths. Once configured, isLive flips and the normal tabs appear.
  const renderSetupSurface = () =>
    isFigmaSite ? (
      <AeroFigmaSourceEditor
        user={user}
        ownerUid={ownerUid || user.uid}
        site={site}
        fetchSite={fetchSite}
        fetchVersions={fetchVersions}
      />
    ) : (
      <SiteCreatePanel
        user={user}
        ownerUid={ownerUid || user.uid}
        site={site}
        deploying={deploying}
        progress={progress}
        onDrop={onDrop}
        onEmbedded={async () => {
          await fetchSite();
          await fetchVersions();
        }}
      />
    );

  return (
    <div className="aero-main aero-sites-page">
      <Helmet>
        <title>{site.title || site.generatedUrl} | Aero Sites</title>
      </Helmet>
      {/* Inside .aero-main so it's the single flex child of .ut-main. */}
      <ToastContainer position="bottom-right" className="aero-toast-container" />

      <SiteDetailHead
        site={site}
        isLive={isLive}
        isShared={isShared}
        access={isShared ? null : siteAccess}
        onUpgrade={openUpgradeModal}
        liveUrl={liveUrl}
        liveHost={liveHost}
        copied={copied}
        onCopy={handleCopy}
        onBack={() => navigate('/sites')}
        editingTitle={editingTitle}
        titleDraft={titleDraft}
        savingTitle={savingTitle}
        onTitleDraft={setTitleDraft}
        onStartTitleEdit={startTitleEdit}
        onSaveTitle={saveTitle}
        onTitleKeyDown={onTitleKeyDown}
        menuItems={[testMenuItem(site, runTestThisSite)].filter(Boolean)}
      />

      <div className="aero-sd-body">
        {isLive ? <SiteOnePage ctx={tabContext} isFigma={isFigmaSite} /> : renderSetupSurface()}
      </div>
    </div>
  );
}

export default AeroSiteDetail;
